/* ============================================================
   DHANSHI FASHION JEWELLERY — Shopify Theme JS
   Shopify Ajax Cart API + improved interactions
   ============================================================ */

const WISHLIST_KEY = 'dhanshi_wishlist_v1';
const moneyFormat = (window.theme && window.theme.moneyFormat) || '₹{{amount}}';

function formatMoney(cents) {
  // Shopify amounts are in cents (or paise for INR shops)
  const value = (cents / 100);
  const formatted = value.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  return moneyFormat.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/g, formatted);
}

/* ====== Wishlist (localStorage — Shopify has no native wishlist) ======
   Storage shape (v2): array of { id, handle }. We keep id for backward compat
   with old entries (which were plain strings), and use handle to fetch product
   data from /products/<handle>.js on the wishlist page. */
function getWishlist() {
  try {
    const raw = JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    // Migrate legacy entries (plain id strings) → { id, handle: '' }
    return raw.map(item => {
      if (typeof item === 'string' || typeof item === 'number') {
        return { id: String(item), handle: '' };
      }
      return { id: String(item.id), handle: item.handle || '' };
    });
  } catch { return []; }
}
function setWishlist(list) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  updateWishlistBadge();
}
function wishlistHas(id) {
  return getWishlist().some(x => x.id === String(id));
}
function toggleWishlist(id, handle) {
  const list = getWishlist();
  const idx = list.findIndex(x => x.id === String(id));
  if (idx === -1) {
    list.push({ id: String(id), handle: handle || '' });
    showToast('Added to wishlist.');
  } else {
    list.splice(idx, 1);
  }
  setWishlist(list);
  // Re-render any open wishlist UI (drawer or full page) after a change.
  if (typeof window.__renderWishlistDrawer === 'function') window.__renderWishlistDrawer();
  if (typeof window.__renderWishlistPage === 'function') window.__renderWishlistPage();
}
function updateWishlistBadge() {
  document.querySelectorAll('[data-wishlist-id]').forEach(el => {
    const id = el.getAttribute('data-wishlist-id');
    const active = wishlistHas(id);
    el.classList.toggle('active', active);
    if (el.classList.contains('product-wishlist') || el.classList.contains('btn-wishlist-large')) {
      el.innerHTML = active ? '♥' : '♡';
    }
  });
  // Update the header wishlist count badge if present.
  const badge = document.querySelector('[data-wishlist-count]');
  if (badge) {
    const n = getWishlist().length;
    badge.textContent = n;
    badge.classList.toggle('is-visible', n > 0);
  }
}

/* ====== Shopify Cart API ====== */
async function fetchCart() {
  const res = await fetch('/cart.js');
  return res.json();
}
async function addToCart(variantId, qty = 1, properties) {
  const body = { id: variantId, quantity: qty };
  if (properties) body.properties = properties;
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || 'Could not add to cart.');
  }
  return res.json();
}
async function updateCartLine(line, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ line, quantity })
  });
  return res.json();
}
async function refreshCartBadge() {
  try {
    const cart = await fetchCart();
    setCartBadge(cart.item_count);
    return cart;
  } catch { /* network error — ignore */ }
}
function setCartBadge(count) {
  document.querySelectorAll('.header-cart-count').forEach(b => {
    b.textContent = count;
    b.classList.toggle('has-items', count > 0);
  });
}
function bumpCartBadge() {
  document.querySelectorAll('.header-cart-count').forEach(b => {
    b.classList.remove('bump');
    void b.offsetWidth;
    b.classList.add('bump');
  });
}

/* ====== Toast ====== */
let toastEl = null, toastTimer = null;
function showToast(html, withCartLink = false) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast';
    document.body.appendChild(toastEl);
  }
  toastEl.innerHTML =
    '<span class="toast-icon">✦</span>' +
    '<div><span>' + html + '</span>' +
    (withCartLink ? ' <a href="/cart">View bag →</a>' : '') +
    '</div>';
  requestAnimationFrame(() => toastEl.classList.add('show'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl.classList.remove('show'), 3200);
}

/* ====== Header scroll shadow ====== */
function initHeaderScroll() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ====== Mobile drawer ====== */
function initMobileDrawer() {
  const burger = document.querySelector('.header-burger');
  const drawer = document.querySelector('.mobile-drawer');
  const overlay = document.querySelector('.mobile-overlay');
  const close = document.querySelector('.mobile-drawer-close');
  if (!burger || !drawer) return;

  const open = () => { drawer.classList.add('is-open'); overlay && overlay.classList.add('is-open'); document.body.style.overflow = 'hidden'; };
  const shut = () => { drawer.classList.remove('is-open'); overlay && overlay.classList.remove('is-open'); document.body.style.overflow = ''; };
  burger.addEventListener('click', open);
  close && close.addEventListener('click', shut);
  overlay && overlay.addEventListener('click', shut);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') shut(); });

  // Mobile drawer accordion submenus
  drawer.querySelectorAll('.m-item > button').forEach(btn => {
    btn.addEventListener('click', () => {
      const isOpen = btn.parentElement.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });
}

/* ====== Scroll reveal ====== */
function initReveal() {
  const targets = document.querySelectorAll('.reveal');
  if (!targets.length || !('IntersectionObserver' in window)) {
    targets.forEach(t => t.classList.add('visible'));
    return;
  }
  const io = new IntersectionObserver(entries => {
    entries.forEach(en => {
      if (en.isIntersecting) {
        en.target.classList.add('visible');
        io.unobserve(en.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
  targets.forEach(t => io.observe(t));
}

/* ====== Filter tabs ====== */
function initFilterTabs() {
  document.querySelectorAll('[data-filter-group]').forEach(group => {
    const sel = group.getAttribute('data-filter-group');
    // Sidebar filter cards use a bare `data-filter-group` as a flag (no
    // selector). Skip those — they're handled by initCollectionFilters.
    if (!sel) return;
    const target = document.querySelector(sel);
    if (!target) return;
    const buttons = group.querySelectorAll('.filter-btn, .chip');
    const cards = target.querySelectorAll('[data-tags]');
    // Optional cap: show only the first N cards that MATCH the active filter.
    // This makes "Featured" pick the top N *from within* the chosen finish,
    // rather than filtering inside a pre-sliced set.
    const limit = parseInt(target.getAttribute('data-filter-limit'), 10) || 0;

    function apply(tag) {
      let shown = 0;
      cards.forEach(card => {
        const tags = (card.getAttribute('data-tags') || '').split(',').map(s => s.trim().toLowerCase());
        const match = tag === 'all' || tags.includes(tag.toLowerCase());
        const within = match && (limit === 0 || shown < limit);
        card.classList.toggle('hidden', !within);
        if (within) shown++;
      });
      const empty = target.parentElement.querySelector('.collection-empty');
      if (empty) empty.classList.toggle('show', shown === 0);
    }

    buttons.forEach(btn => btn.addEventListener('click', () => {
      buttons.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      apply(btn.getAttribute('data-filter') || 'all');
    }));

    // Apply the cap on load too (initial "All"/active state).
    if (limit > 0) {
      const activeBtn = group.querySelector('.filter-btn.active, .chip.active');
      apply(activeBtn ? (activeBtn.getAttribute('data-filter') || 'all') : 'all');
    }
  });
}

/* ====== Sort (client-side for in-page card sort; for real Shopify sort use ?sort_by=) ====== */
function initSort() {
  const sel = document.querySelector('.toolbar-sort select');
  if (!sel) return;
  sel.addEventListener('change', () => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', sel.value);
    location.href = url.toString();
  });
}

/* ====== Product page ====== */
function initProductPage() {
  const root = document.querySelector('[data-product]');
  if (!root) return;

  // Gallery thumbs — swap the main image, keep alt sane.
  const thumbs = root.querySelectorAll('.product-thumb');
  thumbs.forEach(t => t.addEventListener('click', () => {
    thumbs.forEach(x => x.classList.remove('active'));
    t.classList.add('active');
    const thumbImg = t.querySelector('img');
    const fullUrl = t.dataset.full || (thumbImg && (thumbImg.dataset.full || thumbImg.src));
    const mainImg = root.querySelector('.product-main-img img');
    if (mainImg && fullUrl) {
      mainImg.src = fullUrl;
      if (thumbImg && thumbImg.alt) mainImg.alt = thumbImg.alt;
    }
  }));

  // Variant selection — drives hidden input[name=id]
  const variantInput = root.querySelector('input[name="id"]');
  const variantData = root.dataset.variants ? JSON.parse(root.dataset.variants) : null;
  const priceEl = root.querySelector('.product-price-large');
  const availabilityEl = root.querySelector('.product-availability');
  const saveEl = root.querySelector('.product-save');

  // Collect the option groups in the order they appear (option1, option2, option3).
  const optionGroups = Array.from(root.querySelectorAll('.option-swatches'));

  function getChosenValues() {
    // Returns ["Red", "Small"] — positional, matches option1/option2/option3.
    return optionGroups.map(group => {
      const active = group.querySelector('.option-swatch.active');
      return active ? active.dataset.value : null;
    });
  }

  function pickVariant() {
    if (!variantData) return null;
    const chosen = getChosenValues();
    return variantData.find(v => {
      // Shopify's variant JSON exposes options as an array of values, in option order.
      return chosen.every((val, i) => val === null || v.options[i] === val);
    }) || null;
  }

  function renderStockBadge(v) {
    if (!availabilityEl) return;
    if (!v) {
      availabilityEl.innerHTML = '<span class="stock-badge stock-out"><span class="dot"></span> Unavailable combination</span>';
      return;
    }
    if (!v.available) {
      availabilityEl.innerHTML = '<span class="stock-badge stock-out"><span class="dot"></span> Currently sold out</span>';
      return;
    }
    // Shopify variant JSON exposes inventory_management and inventory_quantity.
    const tracked = v.inventory_management && v.inventory_management !== '';
    const qty = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity : null;
    if (tracked && qty !== null && qty > 0 && qty <= 5) {
      availabilityEl.innerHTML = '<span class="stock-badge stock-low"><span class="dot"></span> Only ' + qty + ' left in stock</span>';
    } else {
      availabilityEl.innerHTML = '<span class="stock-badge stock-in"><span class="dot"></span> In stock · Ready to ship</span>';
    }
  }

  function renderSave(v) {
    if (!saveEl || !v) return;
    if (v.compare_at_price && v.compare_at_price > v.price) {
      const saved = v.compare_at_price - v.price;
      const pct = Math.round((saved * 100) / v.compare_at_price);
      saveEl.style.display = '';
      saveEl.textContent = 'You save ' + formatMoney(saved) + ' (' + pct + '% off)';
    } else {
      saveEl.style.display = 'none';
    }
  }

  function maybeSwapImageForVariant(v) {
    if (!v) return;
    const mainImg = root.querySelector('.product-main-img img');
    if (!mainImg) return;
    // Shopify variant JSON exposes:
    //   v.featured_image  → { id, src, alt, ... }  (image-level id)
    //   v.featured_media  → { id, ... }            (media-level id, matches product.media[].id)
    const src = v.featured_image && v.featured_image.src;
    if (!src) return;
    mainImg.src = src;
    // Prefer matching by media id; fall back to image id.
    const mediaId = (v.featured_media && v.featured_media.id) || (v.featured_image && v.featured_image.id);
    if (!mediaId) return;
    const matchingThumb = root.querySelector('.product-thumb[data-media-id="' + mediaId + '"]');
    if (matchingThumb) {
      root.querySelectorAll('.product-thumb').forEach(x => x.classList.remove('active'));
      matchingThumb.classList.add('active');
    }
  }

  // ---- WhatsApp out-of-stock notify (bangles etc.) ----
  const notifyOn = root.dataset.notifyEnabled === 'true';
  const notifyBtn = root.querySelector('[data-notify-whatsapp]');
  const waNumber = root.dataset.waNumber || '';
  const productTitle = root.dataset.productTitle || '';
  const notifyLabel = root.dataset.notifyLabel || 'Notify on WhatsApp';

  function chosenSizeLabel() {
    let label = null;
    optionGroups.forEach(group => {
      const name = (group.dataset.optionName || '').toLowerCase();
      const active = group.querySelector('.option-swatch.active');
      if (name.indexOf('size') > -1 && active) label = active.dataset.value;
    });
    if (!label) {
      label = getChosenValues().filter(Boolean).join(' / ');
    }
    return label;
  }

  function updateNotify(v) {
    if (!notifyOn || !notifyBtn) return;
    const addBtn = root.querySelector('.btn-add-cart');
    const mAdd = document.querySelector('[data-mobile-buy-bar-add]');
    const unavailable = !v || !v.available;
    if (unavailable) {
      const sz = chosenSizeLabel();
      const msg = 'Hi Dhanshi, I would like to be notified when ' + productTitle +
                  (sz ? ' (Size: ' + sz + ')' : '') + ' is back in stock.';
      const href = 'https://wa.me/' + waNumber + '?text=' + encodeURIComponent(msg);
      notifyBtn.href = href;
      notifyBtn.hidden = false;
      if (addBtn) addBtn.hidden = true;
      if (mAdd) {
        mAdd.classList.add('is-notify');
        mAdd.dataset.waHref = href;
        mAdd.disabled = false;
        mAdd.textContent = notifyLabel;
      }
    } else {
      notifyBtn.hidden = true;
      if (addBtn) addBtn.hidden = false;
      if (mAdd) {
        mAdd.classList.remove('is-notify');
        delete mAdd.dataset.waHref;
      }
    }
  }

  function refreshVariantUi() {
    const v = pickVariant();
    const addBtn = root.querySelector('.btn-add-cart');

    if (!v) {
      // No matching variant — disable the button rather than letting Shopify error.
      if (variantInput) variantInput.value = '';
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
      }
      renderStockBadge(null);
      updateNotify(null);
      return;
    }

    if (variantInput) variantInput.value = v.id;
    if (priceEl) {
      let html = '';
      if (v.compare_at_price && v.compare_at_price > v.price) {
        html += '<span class="compare">' + formatMoney(v.compare_at_price) + '</span>';
      }
      html += formatMoney(v.price);
      priceEl.innerHTML = html;
    }
    if (addBtn) {
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? 'Add to Bag' : 'Sold Out';
    }
    renderStockBadge(v);
    renderSave(v);
    maybeSwapImageForVariant(v);
    updateNotify(v);
  }

  // Mark unavailable swatches (combinations that would result in a sold-out variant
  // given the OTHER currently selected options).
  function refreshSwatchAvailability() {
    if (!variantData) return;
    const chosen = getChosenValues();
    optionGroups.forEach((group, gIdx) => {
      group.querySelectorAll('.option-swatch').forEach(s => {
        // What would chosen look like if THIS swatch were selected?
        const test = chosen.slice();
        test[gIdx] = s.dataset.value;
        const match = variantData.find(v =>
          test.every((val, i) => val === null || v.options[i] === val)
        );
        s.classList.toggle('unavailable', !match || !match.available);
      });
    });
  }

  optionGroups.forEach(group => {
    const labelEl = group.parentElement.querySelector('.option-label .selected-value');
    group.querySelectorAll('.option-swatch').forEach(s => s.addEventListener('click', () => {
      group.querySelectorAll('.option-swatch').forEach(x => x.classList.remove('active'));
      s.classList.add('active');
      if (labelEl) labelEl.textContent = s.dataset.label || s.textContent.trim();
      refreshVariantUi();
      refreshSwatchAvailability();
    }));
  });

  // Initial pass to grey out impossible combinations on load.
  refreshSwatchAvailability();
  // Sync the notify button + mobile bar to the initially-selected variant.
  if (notifyOn) updateNotify(pickVariant());

  // Qty
  const qtyInput = root.querySelector('.qty-selector input');
  root.querySelector('.qty-minus')?.addEventListener('click', () => qtyInput.value = Math.max(1, (+qtyInput.value || 1) - 1));
  root.querySelector('.qty-plus')?.addEventListener('click', () => qtyInput.value = (+qtyInput.value || 1) + 1);

  // Accordion
  root.querySelectorAll('.acc-head').forEach(head => head.addEventListener('click', () => {
    const item = head.parentElement;
    const body = item.querySelector('.acc-body');
    const open = item.classList.toggle('is-open');
    body.style.maxHeight = open ? body.scrollHeight + 'px' : '0px';
  }));

  // Wishlist
  const wishBtn = root.querySelector('.btn-wishlist-large');
  if (wishBtn) {
    const id = root.dataset.product;
    const handle = root.dataset.productHandle || '';
    if (wishlistHas(id)) { wishBtn.classList.add('active'); wishBtn.innerHTML = '♥'; }
    wishBtn.setAttribute('data-wishlist-id', id);
    wishBtn.setAttribute('data-wishlist-handle', handle);
    wishBtn.addEventListener('click', () => toggleWishlist(id, handle));
  }

  // Form submit (Add to Bag)
  const form = root.querySelector('form[action*="/cart/add"]');
  if (form) {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const formData = new FormData(form);
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        }).then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.description || 'Cart error'); }); return r.json(); });
        await refreshCartBadge();
        bumpCartBadge();
        showToast('Added <b>' + (root.querySelector('.product-h1')?.textContent || 'item') + '</b> to your bag.', true);
      } catch (err) {
        showToast(err.message || 'Could not add to cart.');
      }
    });
  }
}

/* ====== Quick add on product cards ====== */
async function quickAddVariant(card, el, variantId, name) {
  if (!variantId) { location.href = card.href; return; }
  const isBtn = el.classList.contains('product-quick');
  const prev = el.textContent;
  el.disabled = true;
  if (isBtn) el.textContent = 'Adding…';
  try {
    await addToCart(parseInt(variantId, 10), 1);
    await refreshCartBadge();
    bumpCartBadge();
    showToast('Added <b>' + (name || 'item') + '</b> to your bag.', true);
  } catch (err) {
    showToast(err.message || 'Could not add to cart.');
  } finally {
    el.disabled = false;
    if (isBtn) el.textContent = prev;
  }
}

function initQuickAdd() {
  document.querySelectorAll('.product-quick').forEach(btn => {
    if (btn.classList.contains('product-quick-wa')) return; // WhatsApp button has its own handler
    if (btn.dataset.wired) return; btn.dataset.wired = '1'; // idempotent — safe to re-run after auto-load
    btn.addEventListener('click', async e => {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest('.product-card');
      const pop = card.querySelector('[data-size-pop]');
      if (pop) {
        // Multi-size product — reveal the size picker instead of adding a default size.
        const willOpen = pop.hidden;
        document.querySelectorAll('[data-size-pop]').forEach(p => { if (p !== pop) p.hidden = true; });
        pop.hidden = !willOpen;
        return;
      }
      // Single-variant product — add directly.
      await quickAddVariant(card, btn, card.dataset.variantId, card.dataset.name);
    });
  });

  // Size-chip clicks inside the picker add that exact variant.
  document.querySelectorAll('[data-size-pop] .csp-chip').forEach(chip => {
    if (chip.dataset.wired) return; chip.dataset.wired = '1';
    chip.addEventListener('click', async e => {
      e.preventDefault();
      e.stopPropagation();
      if (chip.disabled) return;
      const card = chip.closest('.product-card');
      const pop = chip.closest('[data-size-pop]');
      await quickAddVariant(card, chip, chip.dataset.variantId, chip.dataset.variantName);
      if (pop) pop.hidden = true;
    });
  });

  // Close any open size picker when tapping elsewhere.
  document.addEventListener('click', e => {
    if (e.target.closest('[data-size-pop]') || e.target.closest('.product-quick')) return;
    document.querySelectorAll('[data-size-pop]').forEach(p => { p.hidden = true; });
  });

  // Wishlist hearts on cards
  document.querySelectorAll('.product-wishlist').forEach(btn => {
    if (btn.dataset.wired) return; btn.dataset.wired = '1';
    btn.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.getAttribute('data-wishlist-id');
      const handle = btn.getAttribute('data-wishlist-handle') || '';
      toggleWishlist(id, handle);
    });
  });
}

/* ====== Cart page (Shopify-rendered) ====== */
function initCartPage() {
  const form = document.querySelector('form[action="/cart"]');
  if (!form) return;

  // Show/clear the "maximum quantity in cart" note for a given line.
  function stockNote(line, show) {
    const note = form.querySelector(`[data-line-stock="${line}"]`);
    if (note) note.hidden = !show;
  }
  // Clamp a requested quantity to the line's stock ceiling (data-max), if any.
  // Returns { qty, capped } so callers can surface the max message.
  function clampToStock(qtyInput, requested) {
    const maxAttr = qtyInput.getAttribute('data-max');
    if (maxAttr === null || maxAttr === '') return { qty: requested, capped: false };
    const max = parseInt(maxAttr, 10);
    if (!isNaN(max) && requested > max) return { qty: max, capped: true };
    return { qty: requested, capped: false };
  }

  form.querySelectorAll('[data-line-change]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const line = parseInt(btn.dataset.line, 10);
      const delta = parseInt(btn.dataset.lineChange, 10);
      const qtyInput = form.querySelector(`input[name="updates[]"][data-line="${line}"]`);
      const requested = Math.max(0, (parseInt(qtyInput.value, 10) || 1) + delta);
      const { qty, capped } = clampToStock(qtyInput, requested);
      if (capped) {
        // Already at the ceiling — reflect it, flag it, and don't hit the server.
        qtyInput.value = qty;
        stockNote(line, true);
        showToast('Maximum available quantity reached ✦');
        if (parseInt(qtyInput.getAttribute('data-current') || '-1', 10) === qty) return;
      } else {
        stockNote(line, false);
      }
      btn.disabled = true;
      try {
        await updateCartLine(line, qty);
        location.reload();
      } catch {
        btn.disabled = false;
      }
    });
  });

  // Manually typed quantity: clamp to stock and commit on change/blur.
  form.querySelectorAll('input[name="updates[]"]').forEach(qtyInput => {
    qtyInput.setAttribute('data-current', qtyInput.value);
    qtyInput.addEventListener('change', async () => {
      const line = parseInt(qtyInput.dataset.line, 10);
      let requested = parseInt(qtyInput.value, 10);
      if (isNaN(requested) || requested < 0) requested = 0;
      const { qty, capped } = clampToStock(qtyInput, requested);
      qtyInput.value = qty;
      stockNote(line, capped);
      if (capped) showToast('Maximum available quantity reached ✦');
      if (qty === parseInt(qtyInput.getAttribute('data-current'), 10)) return;
      try {
        await updateCartLine(line, qty);
        location.reload();
      } catch {}
    });
  });

  form.querySelectorAll('[data-line-remove]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const line = parseInt(btn.dataset.line, 10);
      try {
        await updateCartLine(line, 0);
        location.reload();
      } catch {}
    });
  });

  // ── Discount code ──
  // Applies/removes codes through the Cart AJAX API (/cart/update.js supports a
  // `discount` param). We then read /cart.js, whose discount_codes[] reports each
  // code's `applicable` flag, so we can tell a valid code from a rejected one and
  // show the right message instead of silently reverting.
  const discountInput = form.querySelector('[data-cart-discount-input]');
  const discountApply = form.querySelector('[data-cart-discount-apply]');
  const discountMsg = form.querySelector('[data-cart-discount-msg]');
  const discountChip = form.querySelector('[data-cart-discount-chip]');
  const discountRemove = form.querySelector('[data-cart-discount-remove]');

  function setMsg(text, kind) {
    if (!discountMsg) return;
    discountMsg.textContent = text || '';
    discountMsg.hidden = !text;
    discountMsg.classList.toggle('is-error', kind === 'error');
    discountMsg.classList.toggle('is-ok', kind === 'ok');
  }

  // Show/hide the "Promo applied successfully" indicator + Remove control.
  function showApplied(codeName) {
    if (discountChip) {
      discountChip.classList.remove('is-hidden');
      const nameEl = discountChip.querySelector('[data-cart-discount-code-name]');
      if (nameEl && codeName) nameEl.textContent = codeName;
    }
    if (discountRemove) { discountRemove.disabled = false; discountRemove.textContent = 'Remove'; }
  }
  function hideApplied() {
    if (discountChip) discountChip.classList.add('is-hidden');
  }

  async function setCartDiscount(codeString) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: codeString })
    });
    return res.json();
  }

  if (discountInput && discountApply) {
    const applyDiscount = async () => {
      const code = (discountInput.value || '').trim();
      if (!code) { setMsg('Enter a promo code first.', 'error'); discountInput.focus(); return; }
      discountApply.disabled = true;
      discountApply.textContent = 'Applying…';
      setMsg('', null);
      try {
        const cart = await setCartDiscount(code);
        const codes = (cart && cart.discount_codes) || [];
        const match = codes.find(c => (c.code || '').toUpperCase() === code.toUpperCase());
        if (match && match.applicable) {
          // Remember that THIS code was applied manually in this session, so the
          // load-time guard below doesn't strip it as a lingering/auto discount.
          try {
            const prev = (sessionStorage.getItem('dhanshi_manual_discount') || '').split(',').filter(Boolean);
            if (!prev.map(c => c.toUpperCase()).includes(match.code.toUpperCase())) prev.push(match.code);
            sessionStorage.setItem('dhanshi_manual_discount', prev.join(','));
          } catch {}
          setMsg('Code “' + match.code + '” applied.', 'ok');
          location.reload();
        } else {
          // Reject: strip the bad code so it doesn't linger, and inform the user.
          const stillValid = codes.filter(c => c.applicable).map(c => c.code).join(',');
          await setCartDiscount(stillValid);
          setMsg('Code not applicable.', 'error');
          discountApply.disabled = false;
          discountApply.textContent = 'Apply';
        }
      } catch {
        setMsg('Could not apply the code. Please try again.', 'error');
        discountApply.disabled = false;
        discountApply.textContent = 'Apply';
      }
    };
    discountApply.addEventListener('click', applyDiscount);
    // Enter inside the field applies the code instead of submitting the cart form.
    discountInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); }
    });
  }

  if (discountRemove) {
    discountRemove.addEventListener('click', async () => {
      discountRemove.disabled = true;
      discountRemove.textContent = 'Removing…';
      try {
        try { sessionStorage.removeItem('dhanshi_manual_discount'); } catch {}
        await setCartDiscount('');       // empty string clears all codes
        location.reload();
      } catch {
        discountRemove.disabled = false;
        discountRemove.textContent = 'Remove';
        setMsg('Could not remove the code. Please try again.', 'error');
      }
    });
  }

  // ── Enforce manual-only discounts ──
  // A discount code applied in an earlier session lingers in Shopify's cart
  // session and would otherwise appear "already applied" on every visit —
  // looking auto-applied. On load we strip any applicable code that was NOT
  // applied via the Apply button in THIS browser session, so no discount shows
  // until the shopper types it and clicks Apply.
  // NOTE: Shopify *automatic* discounts (Admin → Discounts, no code required)
  // are not discount codes and cannot be removed from the theme — those must be
  // paused/deleted in the Shopify admin.
  (async function enforceManualDiscount() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      const codes = (cart && cart.discount_codes) || [];
      const applied = codes.filter(c => c.applicable).map(c => c.code);
      if (!applied.length) return;
      const manual = (sessionStorage.getItem('dhanshi_manual_discount') || '')
        .split(',').filter(Boolean).map(c => c.toUpperCase());
      const keep = applied.filter(c => manual.includes(c.toUpperCase()));
      if (keep.length === applied.length) {
        // Every applied code was applied manually — reflect the success state so
        // the shopper always sees confirmation + a Remove control near the box.
        showApplied(applied[0]);
        setMsg('Promo applied successfully.', 'ok');
        return;
      }
      await setCartDiscount(keep.join(','));       // drop the lingering codes
      location.reload();
    } catch {}
  })();
}

/* ====== Newsletter ======
   Both the footer "Join Our Circle" form and the standalone newsletter
   section use Shopify's {% form 'customer' %}, which posts natively to
   Shopify and stores the email as a Customer tagged "newsletter". We must
   NOT preventDefault on those, or the email is silently dropped.

   A form is a real Shopify form if it carries a hidden form_type=customer
   input (or its action points at Shopify's /contact endpoint). In that case
   we let the browser submit it normally. Only a non-Shopify form (no such
   marker) falls back to the old toast-only behaviour. */
function isShopifyCustomerForm(form) {
  const ft = form.querySelector('input[name="form_type"]');
  if (ft && ft.value === 'customer') return true;
  return !!(form.action && form.action.indexOf('/contact') !== -1);
}

function initNewsletter() {
  document.querySelectorAll('.newsletter-form, .mini-news-form').forEach(form => {
    // Real Shopify customer forms: let them submit natively so the email is saved.
    if (isShopifyCustomerForm(form)) return;
    // Fallback (should not normally happen): avoid losing the email.
    form.addEventListener('submit', e => {
      e.preventDefault();
      showToast('Thanks for subscribing ✦ Check your inbox.');
      form.reset();
    });
  });

  // After a successful Shopify newsletter signup, Shopify reloads the page
  // with ?customer_posted=true. Surface a clear, visible confirmation so the
  // subscriber knows it worked (the inline success text may be off-screen).
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true') {
      showToast('Thank you — you’re on the list ✦');
    }
  } catch (err) { /* no-op */ }
}

/* ====== Highlight active nav ====== */
function highlightActiveNav() {
  const path = location.pathname.replace(/\/$/, '');
  document.querySelectorAll('.mega-nav-link, .m-item > a').forEach(a => {
    const href = (a.getAttribute('href') || '').replace(/\/$/, '');
    if (href && (href === path || path.startsWith(href + '/'))) a.classList.add('active');
  });
}

/* ====== Sticky collection toolbar — REMOVED ======
   Previously this function tried to JS-fix a CSS sticky toolbar, but the CSS
   sticky + JS fixed-position fallback ran in parallel and fought each other,
   producing the "filter bar follows the scroll" bug on long collection pages
   like /collections/haram. Both the CSS sticky and this JS shim have been
   removed; the toolbar is now plain static, which is the desired behaviour. */

/* ====== Wishlist drawer ======
   The header heart icon opens this drawer (no /pages/wishlist navigation needed,
   so there's no 404 risk if the merchant hasn't created the page). The drawer
   hydrates from localStorage by fetching each product via /products/<handle>.js. */
function initWishlistDrawer() {
  const drawer = document.getElementById('WishlistDrawer');
  if (!drawer) return;

  const list = document.getElementById('WishlistDrawerList');
  const emptyEl = document.getElementById('WishlistDrawerEmpty');
  const loadingEl = document.getElementById('WishlistDrawerLoading');

  function open() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    render();
  }
  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-wishlist-open]').forEach(btn => {
    btn.addEventListener('click', e => { e.preventDefault(); open(); });
  });
  drawer.querySelectorAll('[data-wishlist-close]').forEach(el => {
    el.addEventListener('click', close);
  });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  async function fetchProduct(handle) {
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }

  function buildItem(product, savedId) {
    const variant = product.variants && product.variants[0];
    const li = document.createElement('li');
    li.className = 'wishlist-drawer-item';
    li.dataset.id = savedId;

    const imgSrc = (product.featured_image || (product.images && product.images[0]) || '')
      .replace(/(\.[a-z]+)(\?|$)/i, '_400x$1$2');

    let priceHtml = '';
    if (product.compare_at_price && product.compare_at_price > product.price) {
      priceHtml += '<span class="original">' + formatMoney(product.compare_at_price) + '</span>';
    }
    priceHtml += formatMoney(product.price);

    const soldOut = !variant || !product.available;

    li.innerHTML = `
      <a class="wishlist-drawer-item-img" href="${product.url}">
        <img src="${imgSrc}" alt="${(product.title || '').replace(/"/g, '&quot;')}" loading="lazy">
      </a>
      <div class="wishlist-drawer-item-info">
        <a class="wishlist-drawer-item-title" href="${product.url}">${product.title || ''}</a>
        <p class="wishlist-drawer-item-type">${product.type || product.vendor || 'Hand-crafted'}</p>
        <p class="wishlist-drawer-item-price">${priceHtml}</p>
        <button type="button" class="wishlist-drawer-item-cart" ${soldOut ? 'disabled' : ''}>
          ${soldOut ? 'Sold out' : 'Add to bag'}
        </button>
      </div>
      <button type="button" class="wishlist-drawer-item-remove" aria-label="Remove">×</button>
    `;

    li.querySelector('.wishlist-drawer-item-remove').addEventListener('click', () => {
      toggleWishlist(savedId);
      render();
    });

    if (!soldOut) {
      const cartBtn = li.querySelector('.wishlist-drawer-item-cart');
      cartBtn.addEventListener('click', async () => {
        cartBtn.disabled = true;
        const orig = cartBtn.textContent;
        cartBtn.textContent = 'Adding…';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variant.id, quantity: 1 })
          });
          if (!res.ok) throw new Error('Cart error');
          await refreshCartBadge();
          bumpCartBadge();
          cartBtn.textContent = 'Added ✓';
          showToast('Added <b>' + (product.title || 'item') + '</b> to your bag.', true);
          setTimeout(() => { cartBtn.disabled = false; cartBtn.textContent = orig; }, 1600);
        } catch {
          cartBtn.textContent = 'Try again';
          setTimeout(() => { cartBtn.disabled = false; cartBtn.textContent = orig; }, 1600);
        }
      });
    }

    return li;
  }

  async function render() {
    const items = getWishlist();
    list.innerHTML = '';
    loadingEl.hidden = false;
    list.hidden = true;
    emptyEl.hidden = true;

    if (!items.length) {
      loadingEl.hidden = true;
      emptyEl.hidden = false;
      return;
    }

    const fetched = await Promise.all(items.map(async it => {
      if (!it.handle) return null;
      const product = await fetchProduct(it.handle);
      return product ? { id: it.id, product } : null;
    }));

    const valid = fetched.filter(Boolean);
    loadingEl.hidden = true;

    if (!valid.length) {
      emptyEl.hidden = false;
      return;
    }

    valid.forEach(({ id, product }) => list.appendChild(buildItem(product, id)));
    list.hidden = false;
  }

  // Expose so the standalone wishlist page (if any) and toggleWishlist() can re-render.
  window.__renderWishlistDrawer = render;
}

/* ====== Header offset — keep CSS spacer + collection sidebar offset
   accurate against the live fixed header height (announcement bar on/off,
   responsive reflow, etc) ====== */
function initHeaderSpacer() {
  const header = document.getElementById('siteHeader');
  if (!header) return;
  const spacers = document.querySelectorAll('[data-header-spacer]');
  const setHeight = () => {
    const h = header.offsetHeight;
    spacers.forEach(s => { s.style.height = h + 'px'; });
    document.documentElement.style.setProperty('--coll-header-offset', h + 'px');
  };
  setHeight();
  window.addEventListener('resize', setHeight, { passive: true });
  // Re-measure if announcement bar is toggled (Shopify editor) or images load
  const ro = new ResizeObserver(setHeight);
  ro.observe(header);
}

/* ====== Search flyout (slide-down search opened by the small search icon) ====== */
function initSearchFlyout() {
  const flyout = document.getElementById('HeaderSearchFlyout');
  if (!flyout) return;
  const toggles = document.querySelectorAll('[data-search-toggle]');
  const input = flyout.querySelector('[data-search-input]');
  const close = () => {
    flyout.classList.remove('is-open');
    flyout.setAttribute('aria-hidden', 'true');
  };
  const open = () => {
    flyout.classList.add('is-open');
    flyout.setAttribute('aria-hidden', 'false');
    setTimeout(() => input && input.focus(), 60);
  };
  toggles.forEach(btn => btn.addEventListener('click', () => {
    flyout.classList.contains('is-open') ? close() : open();
  }));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && flyout.classList.contains('is-open')) close();
  });
}

/* ====== Collection sidebar — accordion groups, price slider, mobile drawer,
   active-chip strip ====== */
function initCollectionFilters() {
  const sidebar = document.getElementById('CollectionSidebar');
  if (!sidebar) return;

  // ── Accordion groups ──
  sidebar.querySelectorAll('[data-filter-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-filter-group]');
      if (group) group.classList.toggle('is-open');
    });
  });

  // ── Dual-handle price slider ──
  sidebar.querySelectorAll('[data-price-slider]').forEach(slider => {
    const minR = slider.querySelector('[data-price-slider-min]');
    const maxR = slider.querySelector('[data-price-slider-max]');
    const range = slider.querySelector('[data-price-range]');
    const group = slider.closest('[data-filter-group]');
    const minI = group.querySelector('[data-price-min]');
    const maxI = group.querySelector('[data-price-max]');
    const lo = +slider.dataset.min, hi = +slider.dataset.max;

    const paintRange = () => {
      const a = Math.min(+minR.value, +maxR.value);
      const b = Math.max(+minR.value, +maxR.value);
      const aPct = ((a - lo) / (hi - lo)) * 100;
      const bPct = ((b - lo) / (hi - lo)) * 100;
      range.style.left = aPct + '%';
      range.style.right = (100 - bPct) + '%';
    };
    paintRange();

    const sync = (changed) => {
      let a = +minR.value, b = +maxR.value;
      if (changed === 'min' && a > b - 1) { a = b - 1; minR.value = a; }
      if (changed === 'max' && b < a + 1) { b = a + 1; maxR.value = b; }
      minI.value = a;
      maxI.value = b;
      paintRange();
    };
    minR.addEventListener('input', () => sync('min'));
    maxR.addEventListener('input', () => sync('max'));
    minI.addEventListener('input', () => { minR.value = minI.value; sync('min'); });
    maxI.addEventListener('input', () => { maxR.value = maxI.value; sync('max'); });
  });

  // ── Active filter chips strip ──
  const chips = document.getElementById('FilterActiveChips');
  const form  = document.getElementById('CollectionFiltersForm');
  if (chips && form) {
    const render = () => {
      chips.innerHTML = '';
      form.querySelectorAll('input[type=checkbox]:checked, input[type=radio]:checked').forEach(inp => {
        if (!inp.value) return;
        const label = (inp.closest('label')?.querySelector('.label')?.textContent || inp.value).trim();
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'filter-active-chip';
        chip.textContent = label;
        chip.addEventListener('click', () => {
          inp.checked = false;
          inp.dispatchEvent(new Event('change', { bubbles: true }));
          render();
        });
        chips.appendChild(chip);
      });
    };
    form.addEventListener('change', render);
    render();
  }

  // ── Auto-apply filters ──
  // Native Shopify storefront filters (the Search & Discovery app) take effect
  // by reloading the collection URL with the selected filter params — Shopify
  // then returns the filtered products. On desktop we submit the GET form the
  // moment a selection changes; on mobile the user batches selections in the
  // drawer and taps “Apply filters”.
  if (form) {
    const isDesktop = () => window.innerWidth > 900;
    const applyFilters = () => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    };
    form.addEventListener('change', (e) => {
      if (!isDesktop()) return;
      const t = e.target;
      // Range-slider handles have no `name` and only mirror the number inputs,
      // so they're skipped here; their own `change` (on release) is wired below.
      if (t && t.name) applyFilters();
    });
    form.querySelectorAll('[data-price-slider-min], [data-price-slider-max]').forEach(r => {
      r.addEventListener('change', () => { if (isDesktop()) applyFilters(); });
    });
  }

  // ── Clear all ──
  document.querySelectorAll('[data-filters-clear]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      form && form.querySelectorAll('input[type=checkbox], input[type=radio]').forEach(i => { i.checked = false; });
      form && form.querySelectorAll('input[type=number]').forEach(i => { i.value = ''; });
      form && form.querySelectorAll('input[type=range][data-price-slider-min]').forEach(i => { i.value = i.min; i.dispatchEvent(new Event('input', { bubbles: true })); });
      form && form.querySelectorAll('input[type=range][data-price-slider-max]').forEach(i => { i.value = i.max; i.dispatchEvent(new Event('input', { bubbles: true })); });
      chips && (chips.innerHTML = '');
      // If filters are actually applied in the URL, reload the clean collection
      // URL (keeping only the current sort) so the grid resets to all products.
      if (form && window.location.search.indexOf('filter') !== -1) {
        const base = form.getAttribute('action') || window.location.pathname;
        const sortInput = form.querySelector('input[name="sort_by"]');
        window.location.href = (sortInput && sortInput.value)
          ? base + '?sort_by=' + encodeURIComponent(sortInput.value)
          : base;
      }
    });
  });

  // ── Mobile drawer open/close ──
  const open = () => { sidebar.classList.add('is-open'); document.body.style.overflow = 'hidden'; };
  const close = () => { sidebar.classList.remove('is-open'); document.body.style.overflow = ''; };
  document.querySelectorAll('[data-filters-open]').forEach(b => b.addEventListener('click', open));
  document.querySelectorAll('[data-filters-close]').forEach(b => b.addEventListener('click', close));
}

/* ====== Init ====== */
/* ====== Collection: single-page listing + global sold-out-at-bottom ======
   Removes numbered pagination. Fetches every remaining page, appends the
   cards into #collection-grid, re-wires the new cards, and keeps all
   sold-out pieces at the very bottom across the whole collection — on both
   mobile and desktop, for every collection. (Added 28 Oct 2026) */
function sortSoldOutToBottom(grid) {
  if (!grid) return;
  const cards = Array.from(grid.querySelectorAll(':scope > .product-card'));
  if (!cards.length) return;
  const frag = document.createDocumentFragment();
  // Available first (keeps their existing order), then sold-out (keeps theirs).
  cards.forEach(c => { if (c.dataset.available !== 'false') frag.appendChild(c); });
  cards.forEach(c => { if (c.dataset.available === 'false') frag.appendChild(c); });
  grid.appendChild(frag); // moving existing nodes preserves their event listeners
}

function initCollectionAutoload() {
  const grid = document.getElementById('collection-grid');
  if (!grid) return;

  // Always normalise the first (server-rendered) page immediately.
  sortSoldOutToBottom(grid);

  const sentinel = document.querySelector('.collection-autoload');
  let nextUrl = sentinel ? sentinel.getAttribute('data-next-url') : null;
  if (!sentinel || !nextUrl) return; // single-page collection — nothing to load

  let guard = 0; // hard safety stop (handles very large collections without runaway loops)

  const loadNext = async () => {
    if (!nextUrl || guard++ > 60) {
      sentinel.remove();
      sortSoldOutToBottom(grid);
      return;
    }
    try {
      const res = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('page fetch failed');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.getElementById('collection-grid');

      if (newGrid) {
        Array.from(newGrid.querySelectorAll(':scope > .product-card'))
          .forEach(card => grid.appendChild(card));
      }

      // Wire the freshly appended cards and refresh their state.
      initQuickAdd();
      updateWishlistBadge();
      // Keep sold-out at the bottom as each batch arrives.
      sortSoldOutToBottom(grid);

      // Chain to the following page (if any) using the fetched page's own sentinel.
      const nextSentinel = doc.querySelector('.collection-autoload');
      nextUrl = nextSentinel ? nextSentinel.getAttribute('data-next-url') : null;
      loadNext();
    } catch (e) {
      // On any failure, stop gracefully — what's already shown stays usable.
      sentinel.remove();
      sortSoldOutToBottom(grid);
    }
  };

  loadNext();
}

document.addEventListener('DOMContentLoaded', () => {
  initHeaderSpacer();
  initHeaderScroll();
  initSearchFlyout();
  initMobileDrawer();
  initReveal();
  initFilterTabs();
  initSort();
  initCollectionFilters();
  initCollectionAutoload();
  initProductPage();
  initQuickAdd();
  initCartPage();
  initNewsletter();
  initWishlistDrawer();
  highlightActiveNav();
  updateWishlistBadge();
  refreshCartBadge();
});

/* Expose hooks used by section-level inline scripts (wishlist page, etc.) */
window.refreshCartBadge = refreshCartBadge;
window.toggleWishlist = toggleWishlist;
window.getWishlist = getWishlist;
window.wishlistHas = wishlistHas;
